<template>
  <Scroller>
    <!-- Static sections -->
    <transition name="slide-fade" mode="out-in">
      <ScrollerSection sticky="center" id="intro" v-if="!active">
        <div>
          <h1 class="f-title-5">
            Progressing towards universal access to modern energy
          </h1>
          <ScrollIcon>Keep scrolling to read more</ScrollIcon>
        </div>
      </ScrollerSection>
      <ScrollerSection
        behind
        id="modernEnergyAccess"
        key="modernEnergyAccess"
        sticky="right"
        v-if="['ModernEnergyAccess'].includes(active)"
      >
        <div style="display: flex; flex-flow: column; width: 100%">
          <SquareShrinks
            :data="data[active]"
            :max="2.38"
            title="Billion people with out access to..."
          />
          <StaticLegendTest
            v-if="'ModernEnergyAccess' === active"
            spread
            :items="[
              {
                items: [
                  { label: '2021', color: 'historic' },
                  { label: 'STEPS 2030', color: 'STEPS' },
                  { label: 'APS 2030', color: 'APS' },
                  { label: 'NZE 2030', color: 'NZE' },
                ],
              },
            ]"
          />
        </div>
      </ScrollerSection>
      <ScrollerSection
        behind
        id="decouplingEmissions"
        key="decouplingEmissions"
        sticky="right"
        v-if="['DecouplingEmissions'].includes(active)"
      >
        <div class="chart__container">
          <div style="display: flex; justify-content: space-between">
            <p class="m-chart__units">EJ</p>
            <p class="m-chart__units">Gt CO2</p>
          </div>
          <StagedChart
            :baseOptions="baseOptions"
            :stages="data[active]"
            :stage="0"
          />
          <StaticLegendTest
            v-if="'DecouplingEmissions' === active"
            spread
            :items="[
              {
                items: decouplingLegend,
              },
            ]"
          />
        </div>
      </ScrollerSection>
    </transition>
    <!-- Normal sections -->
    <ScrollerSection
      id="ModernEnergyAccess"
      @enter="handleEnter"
      @leave="handleLeave"
    >
      <LeftBlock>
        <p>
          Progress made in the STEPS by 2030 falls short of countries’ targets
          in the APS. The APS is a little over halfway to reaching SDG 7.1:
          universal access to clean cooking and electricity.
        </p>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="DecouplingEmissions" @enter="handleEnter">
      <LeftBlock title="Decoupling emissions from growing demand">
        <p>
          Renewables are the fastest growing energy source in many regions, and
          CO2 emissions fall to 36.2 Gt in the STEPS – slightly below current
          levels, and to 31.5 Gt in the APS, a 14% reduction compared to current
          levels.
        </p>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="fin" @enter="handleEnter" />
  </Scroller>
</template>

<script>
import {
  Scroller,
  ScrollerSection,
  ScrollIcon,
} from "@international-energy-agency/vue-ui";
import ActiveMixin from "../mixins/active";
import SquareShrinks from "../components/SquareShrinks";
import LeftBlock from "../components/LeftBlock.vue";
import StaticLegendTest from "../components/StaticLegendTest.vue";
import StagedChart from "../components/StagedChart.vue";
export default {
  mixins: [ActiveMixin],
  components: {
    Scroller,
    ScrollerSection,
    ScrollIcon,
    LeftBlock,
    StaticLegendTest,
    SquareShrinks,
    StagedChart,
  },
  data() {
    return {
      data: {
        ModernEnergyAccess: [
          {
            title: "Clean cooking",
            label: true,
            pos: "btm-r",
            data: [
              {
                scenario: 2021,
                value: 2.38,
                color: "historic",
              },
              {
                scenario: "STEPS 2030",
                value: 1.88,
                color: "STEPS",
              },
              {
                scenario: "APS 2030",
                value: 0.78,
                color: "APS",
              },
              {
                scenario: "NZE 2030",
                value: 0.05,
                color: "NZE",
              },
            ],
          },
          {
            title: "Electricity",
            pos: "btm-l",
            smaller: true,
            data: [
              {
                scenario: 2021,
                value: 0.75,
                offset: true,
                color: "historic",
              },
              {
                scenario: "STEPS 2030",
                value: 0.66,
                color: "STEPS",
              },
              {
                scenario: "APS 2030",
                value: 0.29,
                color: "APS",
              },
              {
                scenario: "NZE 2030",
                value: 0.05,
                color: "NZE",
              },
            ],
          },
        ],
        DecouplingEmissions: [
          {
            series: [
              {
                region: "International bunkers",
                stack: "2010-19",
                id: "International bunkers2010-19",
                className: "no-stroke color-darkgray",
                data: [0, 2681.477928, 8.131413398, 10.57707083, 0],
              },
              {
                region: "Middle East",
                stack: "2010-19",
                id: "Middle East2010-19",
                className: "no-stroke color-yellow",
                data: [-5.67395136, 1759.167756, 5457.870612, 86.67555228, 0],
              },
              {
                region: "Middle East",
                stack: "STEPS 2021-30",
                id: "Middle EastSTEPS 2021-30",
                className: "no-stroke color-yellow",
                data: [91.74660444, 1515.495996, 4259.692188, 771.324953, 0],
              },
              {
                region: "Middle East",
                stack: "APS 2021-30",
                id: "Middle EastAPS 2021-30",
                className: "no-stroke color-yellow",
                data: [42.75057744, -25.288272, 2477.12022, 1719.844493, 0],
              },
              {
                region: "International bunkers",
                stack: "STEPS 2021-30",
                id: "International bunkersSTEPS 2021-30",
                className: "no-stroke color-darkgray",
                data: [0, 5468.463216, 362.0119132, 231.56366, 0],
              },
              {
                region: "International bunkers",
                stack: "APS 2021-30",
                id: "International bunkersAPS 2021-30",
                className: "no-stroke color-darkgray",
                data: [0, 4138.107516, 253.4557254, 729.2783023, 0],
              },

              {
                region: "Other emerging market and developing economies",
                stack: "2010-19",
                id: "Other emerging market and developing economies2010-19",
                className: "no-stroke color-lightpurple",
                data: [1156.683887, 348.634836, 1351.633018, 1935.950781, 0],
              },
              {
                region: "Other emerging market and developing economies",
                stack: "STEPS 2021-30",
                id: "Other emerging market and developing economiesSTEPS 2021-30",
                className: "no-stroke color-lightpurple",
                data: [-1146.192184, 2003.467536, -1068.488107, 4038.470887, 0],
              },

              {
                region: "Other emerging market and developing economies",
                stack: "APS 2021-30",
                id: "Other emerging market and developing economiesAPS 2021-30",
                className: "no-stroke color-lightpurple",
                data: [-2180.660448, 764.216604, -3032.783942, 8397.264631, 0],
              },
              {
                region: "Africa",
                stack: "2010-19",
                id: "Africa2010-19",
                className: "no-stroke color-historic",
                data: [151.938972, 1235.94336, 2042.806709, 1389.544492, 0],
              },
              {
                region: "Africa",
                stack: "STEPS 2021-30",
                id: "AfricaSTEPS 2021-30",
                className: "no-stroke color-historic",
                data: [-131.632992, 2055.593196, 1491.12882, 3891.337524, 0],
              },
              {
                region: "Africa",
                stack: "APS 2021-30",
                id: "AfricaAPS 2021-30",
                className: "no-stroke color-historic",
                data: [-970.9147332, 1866.977856, 585.775188, 4328.397576, 0],
              },
              {
                region: "Other advanced economies",
                stack: "2010-19",
                id: "Other advanced economies2010-19",
                className: "no-stroke color-green",
                data: [-1252.523088, -1078.142868, 2950.605432, 2627.384472, 0],
              },
              {
                region: "Other advanced economies",
                stack: "STEPS 2021-30",
                id: "Other advanced economiesSTEPS 2021-30",
                className: "no-stroke color-green",
                data: [-3285.679223, -832.75452, -2127.31308, 4721.789304, 0],
              },
              {
                region: "Other advanced economies",
                stack: "APS 2021-30",
                id: "Other advanced economiesAPS 2021-30",
                className: "no-stroke color-green",
                data: [
                  -5631.903328, -4433.151312, -4399.321968, 9546.197076, 0,
                ],
              },
              {
                region: "European Union",
                stack: "2010-19",
                id: "European Union2010-19",
                className: "no-stroke color-teal",
                data: [-4667.063641, -5492.830392, -6190.979292, 7445.1771, 0],
              },
              {
                region: "European Union",
                stack: "STEPS 2021-30",
                id: "European UnionSTEPS 2021-30",
                className: "no-stroke color-teal",
                data: [-3169.030788, -1929.235572, -1204.12368, 2306.382516, 0],
              },
              {
                region: "European Union",
                stack: "APS 2021-30",
                id: "European UnionAPS 2021-30",
                className: "no-stroke color-teal",
                data: [
                  -3291.331403, -3135.536388, -2849.410476, 3996.844884, 0,
                ],
              },
              {
                region: "United States",
                stack: "2010-19",
                id: "United States2010-19",
                className: "no-stroke color-blue",
                data: [-9485.069796, -484.83144, 7789.792608, 2873.61018, 0],
              },
              {
                region: "United States",
                stack: "STEPS 2021-30",
                id: "United StatesSTEPS 2021-30",
                className: "no-stroke color-blue",
                data: [-7946.328686, -1492.552332, -503.378964, 5874.959628, 0],
              },
              {
                region: "United States",
                stack: "APS 2021-30",
                id: "United StatesAPS 2021-30",
                className: "no-stroke color-blue",
                data: [-8727.652555, -4833.325656, -5618.89494, 10855.95372, 0],
              },

              {
                region: "Southeast Asia",
                stack: "2010-19",
                id: "Southeast Asia2010-19",
                className: "no-stroke color-orange",
                data: [3857.114621, 2312.913924, 909.079884, 2114.170715, 0],
              },
              {
                region: "Southeast Asia",
                stack: "STEPS 2021-30",
                id: "Southeast AsiaSTEPS 2021-30",
                className: "no-stroke color-orange",
                data: [2005.309728, 3462.106788, 1403.499096, 3249.040536, 0],
              },
              {
                region: "Southeast Asia",
                stack: "APS 2021-30",
                id: "Southeast AsiaAPS 2021-30",
                className: "no-stroke color-orange",
                data: [775.855908, 2113.371036, 1090.996344, 4900.523796, 0],
              },
              {
                region: "India",
                stack: "2010-19",
                id: "India2010-19",
                className: "no-stroke color-purple",
                data: [5371.831872, 3073.069332, 45.5021424, 2275.383449, 0],
              },
              {
                region: "India",
                stack: "STEPS 2021-30",
                id: "IndiaSTEPS 2021-30",
                className: "no-stroke color-purple",
                data: [4698.133884, 3735.04428, 1784.514643, 4072.29102, 0],
              },
              {
                region: "India",
                stack: "APS 2021-30",
                id: "IndiaAPS 2021-30",
                className: "no-stroke color-purple",
                data: [2680.473096, 2157.2487, 1599.26549, 5358.47598, 0],
              },
              {
                region: "China",
                stack: "2010-19",
                id: "China2010-19",
                className: "no-stroke color-red",
                data: [11810.12544, 9219.501072, 6594.477955, 6696.242316, 0],
              },
              {
                region: "China",
                stack: "STEPS 2021-30",
                id: "ChinaSTEPS 2021-30",
                className: "no-stroke color-red",
                data: [-5381.71272, 1347.019164, 2052.36936, 11818.08036, 0],
              },
              {
                region: "China",
                stack: "APS 2021-30",
                id: "ChinaAPS 2021-30",
                className: "no-stroke color-red",
                data: [-13661.94708, -689.272884, 762.62562, 14619.17516, 0],
              },
              {
                region: "International bunkers",
                stack: "2010-19",
                id: "International bunkers2010-19",
                className: "no-stroke color-darkgray",
                yAxis: 1,
                data: [0, 0, 0, 0, 191.1],
              },
              {
                region: "Other emerging market and developing economies",
                stack: "2010-19",
                id: "Other emergiing market and developing economies2010-19",
                className: "no-stroke color-lightpurple",
                yAxis: 1,
                data: [0, 0, 0, 0, 172.75],
              },
              {
                region: "International bunkers",
                stack: "STEPS 2021-30",
                id: "International bunkersSTEPS 2021-30",
                className: "no-stroke color-darkgray",
                yAxis: 1,
                data: [0, 0, 0, 0, 409.74],
              },
              {
                region: "International bunkers",
                stack: "APS 2021-30",
                id: "International bunkersAPS 2021-30",
                className: "no-stroke color-darkgray",
                yAxis: 1,
                data: [0, 0, 0, 0, 304.2],
              },
              {
                region: "Middle East",
                stack: "2010-19",
                id: "Middle East2010-19",
                className: "no-stroke color-yellow",
                yAxis: 1,
                data: [0, 0, 0, 0, 358.87],
              },
              {
                region: "Africa",
                stack: "2010-19",
                id: "Africa2010-19",
                className: "no-stroke color-historic",
                yAxis: 1,
                data: [0, 0, 0, 0, 252.6],
              },

              {
                region: "Middle East",
                stack: "STEPS 2021-30",
                id: "Middle EastSTEPS 2021-30",
                className: "no-stroke color-yellow",
                yAxis: 1,
                data: [0, 0, 0, 0, 234.41],
              },
              {
                region: "Middle East",
                stack: "APS 2021-30",
                id: "Middle EastAPS 2021-30",
                className: "no-stroke color-yellow",
                yAxis: 1,
                data: [0, 0, 0, 0, -20.93],
              },

              {
                region: "Africa",
                stack: "STEPS 2021-30",
                id: "AfricaSTEPS 2021-30",
                className: "no-stroke color-historic",
                yAxis: 1,
                data: [0, 0, 0, 0, 220.73],
              },
              {
                region: "Africa",
                stack: "APS 2021-30",
                id: "AfricaAPS 2021-30",
                className: "no-stroke color-historic",
                yAxis: 1,
                data: [0, 0, 0, 0, 57.38],
              },
              {
                region: "Southeast Asia",
                stack: "2010-19",
                id: "Southeast Asia2010-19",
                className: "no-stroke color-orange",
                yAxis: 1,
                data: [0, 0, 0, 0, 572.8],
              },
              {
                region: "Southeast Asia",
                stack: "STEPS 2021-30",
                id: "Southeast AsiaSTEPS 2021-30",
                className: "no-stroke color-orange",
                yAxis: 1,
                data: [0, 0, 0, 0, 462.97],
              },
              {
                region: "Southeast Asia",
                stack: "APS 2021-30",
                id: "Southeast AsiaAPS 2021-30",
                className: "no-stroke color-orange",
                yAxis: 1,
                data: [0, 0, 0, 0, 228.89],
              },
              {
                region: "India",
                stack: "2010-19",
                id: "India2010-19",
                className: "no-stroke color-purple",
                yAxis: 1,
                data: [0, 0, 0, 0, 764.73],
              },
              {
                region: "India",
                stack: "STEPS 2021-30",
                id: "IndiaSTEPS 2021-30",
                className: "no-stroke color-purple",
                yAxis: 1,
                data: [0, 0, 0, 0, 803.05],
              },
              {
                region: "India",
                stack: "APS 2021-30",
                id: "IndiaAPS 2021-30",
                className: "no-stroke color-purple",
                yAxis: 1,
                data: [0, 0, 0, 0, 461.65],
              },
              {
                region: "Other emerging market and developing economies",
                stack: "STEPS 2021-30",
                id: "Other emergiing market and developing economiesSTEPS 2021-30",
                className: "no-stroke color-lightpurple",
                yAxis: 1,
                data: [0, 0, 0, 0, -38.56],
              },
              {
                region: "Other emerging market and developing economies",
                stack: "APS 2021-30",
                id: "Other emergiing market and developing economiesAPS 2021-30",
                className: "no-stroke color-lightpurple",
                yAxis: 1,
                data: [0, 0, 0, 0, -385.09],
              },
              {
                region: "China",
                stack: "2010-19",
                id: "China2010-19",
                className: "no-stroke color-red",
                yAxis: 1,
                data: [0, 0, 0, 0, 2563.45],
              },
              {
                region: "China",
                stack: "STEPS 2021-30",
                id: "ChinaSTEPS 2021-30",
                className: "no-stroke color-red",
                yAxis: 1,
                data: [0, 0, 0, 0, -288.8],
              },
              {
                region: "China",
                stack: "APS 2021-30",
                id: "ChinaAPS 2021-30",
                className: "no-stroke color-red",
                yAxis: 1,
                data: [0, 0, 0, 0, -1532.2],
              },
              {
                region: "European Union",
                stack: "STEPS 2021-30",
                id: "European UnionSTEPS 2021-30",
                className: "no-stroke color-teal",
                yAxis: 1,
                data: [0, 0, 0, 0, -488.97],
              },
              {
                region: "European Union",
                stack: "2010-19",
                id: "European Union2010-19",
                className: "no-stroke color-teal",
                yAxis: 1,
                data: [0, 0, 0, 0, -1257.94],
              },
              {
                region: "European Union",
                stack: "APS 2021-30",
                id: "European UnionAPS 2021-30",
                className: "no-stroke color-teal",
                yAxis: 1,
                data: [0, 0, 0, 0, -706.8],
              },
              {
                region: "United States",
                stack: "2010-19",
                id: "United States2010-19",
                className: "no-stroke color-blue",
                yAxis: 1,
                data: [0, 0, 0, 0, -590.05],
              },
              {
                region: "United States",
                stack: "STEPS 2021-30",
                id: "United StatesSTEPS 2021-30",
                className: "no-stroke color-blue",
                yAxis: 1,
                data: [0, 0, 0, 0, -1027.61],
              },
              {
                region: "United States",
                stack: "APS 2021-30",
                id: "United StatesAPS 2021-30",
                className: "no-stroke color-blue",
                yAxis: 1,
                data: [0, 0, 0, 0, -1749.54],
              },
            ],
          },
        ],
      },
    };
  },
  computed: {
    decouplingLegend() {
      let data = this.data.DecouplingEmissions[0].series;
      let unique = [
        ...new Map(data.map((item) => [item["region"], item])).values(),
      ];
      return unique.map((d) => ({
        label: d.region,
        color: d.className.split("-")[2],
      }));
    },
    baseOptions() {
      return {
        chart: {
          type: "column",
          // className: "demand-column-stacked",
          height: 400,
          width: null,
          margin: [20, 30, 80, 30],
          spacing: [0, 0, 0, 0],
        },
        exporting: {
          enabled: false,
        },
        plotOptions: {
          column: {
            stacking: "normal",
          },
          series: {
            // borderWidth: 0,
            label: {
              connectorAllowed: false,
            },
            states: {
              hover: {
                enabled: false,
              },
            },
            animation: false,
          },
        },
        yAxis: [
          {
            tickAmount: 5,
            labels: {
              formatter: function (e) {
                return e.value / 1000;
              },
            },
            title: {
              text: null,
            },
          },
          {
            tickAmount: 5,
            opposite: true,
            labels: {
              formatter: function (e) {
                return e.value / 1000;
              },
            },
            title: {
              text: null,
            },
            plotLines: [
              {
                value: 0,
                className: "highcharts-zero-axis",
                zIndex: 99,
              },
            ],
          },
        ],
        xAxis: {
          categories: [
            "Coal",
            "Oil",
            "Natural gas",
            "Renewables",
            "CO2 emissions",
          ],
          tickLength: 0,
          plotLines: [
            {
              value: 3.5,
              className: "highcharts-dotted",
              zIndex: 99,
            },
            {
              color: "red",
              className: "no-stroke",
              value: -0.2,
              label: {
                text: "2010-19",
                y: 100,
                rotation: -90,
              },
              zIndex: 99,
            },
            {
              color: "red",
              className: "no-stroke",
              value: 0,
              label: {
                text: "STEPS 2021-30",
                y: 130,
                rotation: -90,
              },
              zIndex: 99,
            },
            {
              color: "red",
              className: "no-stroke",
              value: 0.2,
              label: {
                text: "APS 2021-30",
                y: 140,
                rotation: -90,
              },
              zIndex: 99,
            },
          ],
        },
        legend: {
          enabled: false,
        },
        tooltip: {
          enabled: false,
        },
      };
    },
  },
};
</script>

<style scoped>
@import url("../css/main.css");
@import url("../css/colors.css");
.demand-column-stacked-container {
  display: flex;
  flex-flow: column;
  width: 100%;
}
</style>
